import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Pagination
} from '@mui/material';
import {
  Close as CloseIcon,
  Folder as FolderIcon,
  Image as ImageIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import api from '../services/api';
import { S3Object, S3Folder, S3BrowseResult } from '../types';

// =============================================================================
// INTERFACES
// =============================================================================
interface S3ImageBrowserProps {
  open: boolean;
  onClose: () => void;
  onSelectImage: (imageUrl: string) => void;
  title?: string;
}

interface BreadcrumbItem {
  name: string;
  prefix: string;
}

// =============================================================================
// COMPONENT
// =============================================================================
export const S3ImageBrowser: React.FC<S3ImageBrowserProps> = ({
  open,
  onClose,
  onSelectImage,
  title = 'Browse Images'
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [images, setImages] = useState<S3Object[]>([]);
  const [folders, setFolders] = useState<S3Folder[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // =============================================================================
  // EFFECTS
  // =============================================================================
  useEffect(() => {
    if (open) {
      loadBucketContents();
    }
  }, [open, currentPrefix]);

  useEffect(() => {
    updateBreadcrumbs();
  }, [currentPrefix]);

  // =============================================================================
  // HELPER FUNCTIONS
  // =============================================================================
  const updateBreadcrumbs = () => {
    if (!currentPrefix) {
      setBreadcrumbs([]);
      return;
    }

    const parts = currentPrefix.split('/').filter(Boolean);
    const crumbs: BreadcrumbItem[] = [];
    let prefix = '';

    parts.forEach((part, index) => {
      prefix += part + '/';
      crumbs.push({
        name: part,
        prefix: prefix
      });
    });

    setBreadcrumbs(crumbs);
  };

  const loadBucketContents = async (continuationToken?: string) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        prefix: currentPrefix,
        imagesOnly: 'true',
        loadAll: 'true' // Load all images for better search functionality
      });

      // Note: When loadAll is true, continuationToken is ignored by the backend
      const response = await api.get(`/upload/browse?${params}`);
      const data: S3BrowseResult = response.data;

      setImages(data.objects);
      setFolders(data.folders);
      setHasMore(data.hasMore);
      setNextToken(data.nextToken);
      setTotalPages(Math.ceil(data.objects.length / 50)); // 50 images per page
    } catch (err: any) {
      console.error('Failed to load S3 contents:', err);
      setError(err.response?.data?.error || 'Failed to load bucket contents');
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folder: S3Folder) => {
    setCurrentPrefix(folder.prefix);
    setPage(1);
  };

  const handleBreadcrumbClick = (prefix: string) => {
    setCurrentPrefix(prefix);
    setPage(1);
  };

  const handleHomeClick = () => {
    setCurrentPrefix('');
    setPage(1);
  };

  const handleImageSelect = (image: S3Object) => {
    setSelectedImage(image.url);
  };

  const handleConfirmSelection = () => {
    if (selectedImage) {
      onSelectImage(selectedImage);
      onClose();
    }
  };



  const filteredImages = images.filter(image =>
    image.fileName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedImages = filteredImages.slice((page - 1) * 50, page * 50);
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // =============================================================================
  // RENDER
  // =============================================================================
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Navigation */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Tooltip title="Home">
              <IconButton onClick={handleHomeClick} size="small">
                <HomeIcon />
              </IconButton>
            </Tooltip>
            <Breadcrumbs sx={{ ml: 1 }}>
              {breadcrumbs.map((crumb, index) => (
                <Link
                  key={index}
                  component="button"
                  variant="body2"
                  onClick={() => handleBreadcrumbClick(crumb.prefix)}
                  sx={{ textDecoration: 'none' }}
                >
                  {crumb.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Content */}
        {loading && images.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Folders */}
            {folders.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Folders ({folders.length})
                </Typography>
                <Grid container spacing={2}>
                  {folders.map((folder) => (
                    <Grid item xs={6} sm={4} md={3} key={folder.prefix}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handleFolderClick(folder)}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                          <FolderIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body2" noWrap>
                            {folder.name}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Images */}
            {filteredImages.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Images ({filteredImages.length})
                  {selectedImage && (
                    <Chip
                      label="1 selected"
                      color="primary"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Grid container spacing={2}>
                  {paginatedImages.map((image) => (
                    <Grid item xs={6} sm={4} md={3} key={image.key}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          position: 'relative',
                          border: selectedImage === image.url ? 2 : 1,
                          borderColor: selectedImage === image.url ? 'primary.main' : 'divider',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => handleImageSelect(image)}
                      >
                        <CardMedia
                          component="img"
                          height="120"
                          image={image.url}
                          alt={image.fileName}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" noWrap>
                            {image.fileName}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {(image.size / 1024).toFixed(1)} KB
                          </Typography>
                        </CardContent>
                        {selectedImage === image.url && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              p: 0.5
                            }}
                          >
                            <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                          </Box>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {Math.ceil(filteredImages.length / 50) > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={Math.ceil(filteredImages.length / 50)}
                      page={page}
                      onChange={(_, newPage) => setPage(newPage)}
                      color="primary"
                    />
                  </Box>
                )}


              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  {searchTerm ? 'No images match your search' : 'No images found in this folder'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirmSelection}
          variant="contained"
          disabled={!selectedImage}
        >
          Select Image
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 